<template>
    <div>
        <v-progress-linear v-if="loading" indeterminate height="2"></v-progress-linear>
        <template v-if="data">
            <div v-if="!staffId && data.self_entry_open" class="caption mb-3">
                Agree your plans with your section head, enter them here, then send each one for approval by
                <strong>{{ fmtFullDate(data.deadline) }}</strong>. Each type is approved on its own.
            </div>

            <v-card v-for="p in data.plans" :key="p.type" outlined class="mb-3">
                <div class="d-flex align-center flex-wrap px-3 pt-2 pb-1" style="gap: 8px;">
                    <span class="subtitle-2"><span class="type-dot" :style="{ background: p.color }"></span>{{ p.label }}</span>
                    <v-chip x-small label :color="statusColor(p.status)" text-color="white">{{ statusText(p.status) }}</v-chip>
                    <span class="caption grey--text">{{ trail(p) }}</span>
                    <v-spacer></v-spacer>
                    <v-btn v-if="p.permissions.can_withdraw" small text :loading="acting === p.type + ':withdraw'" @click="withdraw(p)">
                        Withdraw
                    </v-btn>
                    <v-btn v-if="p.permissions.can_submit" small color="primary" :loading="acting === p.type + ':submit'" @click="submit(p)">
                        <v-icon small left>mdi-send</v-icon>Send for approval
                    </v-btn>
                    <v-btn v-if="p.permissions.can_cancel" small text color="error" @click="openCancel(p)">
                        Cancel plan
                    </v-btn>
                    <v-btn v-if="p.permissions.can_return" small text color="error" @click="openReturn(p)">
                        Send back
                    </v-btn>
                    <v-btn v-if="p.permissions.can_approve" small color="success" :loading="acting === p.type + ':approve'" @click="approve(p)">
                        <v-icon small left>mdi-check</v-icon>Approve
                    </v-btn>
                </div>

                <div class="px-3">
                    <v-alert v-if="p.returned" type="warning" dense text class="mb-2 caption">
                        Sent back by {{ p.returned.by }} · {{ p.returned.at }} — {{ p.returned.reason }}
                    </v-alert>
                    <v-alert v-if="p.permissions.locked_reason" type="info" dense text class="mb-2 caption">
                        {{ p.permissions.locked_reason }}
                    </v-alert>
                    <v-alert v-else-if="p.permissions.needs_reason" type="warning" dense text class="mb-2 caption">
                        <template v-if="p.status === 'approved'">Approved — change it only where necessary.</template>
                        <template v-else-if="p.permissions.by_hr">What you enter is approved straight away.</template>
                        You'll be asked for a reason, and the staff member is notified.
                    </v-alert>
                </div>

                <!-- Work From Home: one day per Sun–Thu week -->
                <template v-if="p.is_wfh">
                    <div class="caption grey--text px-3">One day per week (Sunday – Thursday). A week running into the next month is planned in the month it starts. Click a day to choose it, click it again to clear it.</div>
                    <div v-if="!staffId && p.status === 'approved'" class="caption grey--text px-3">
                        Once approved, ask your section head to move a day within its week, or tell HR you're coming to the office instead (until {{ noticeUntil }} on the day). A week's WFH doesn't carry over.
                    </div>
                    <v-simple-table dense class="mb-1">
                        <tbody>
                            <template v-for="week in data.weeks">
                            <tr :key="week.week_start" :class="{ 'has-day': dayShown(p, week) }">
                                <td class="caption grey--text text-no-wrap" style="width: 1px;">Week of {{ fmtDate(week.week_start) }}</td>
                                <td class="py-1">
                                    <div v-if="week.blocked" class="caption grey--text">
                                        <v-icon x-small color="grey">mdi-cancel</v-icon>
                                        {{ blockedText(week) }}
                                    </div>
                                    <div v-else class="d-flex flex-wrap" style="gap: 6px;">
                                        <v-btn
                                            v-for="date in week.dates" :key="date"
                                            x-small depressed
                                            :color="wfhEntry(p, date) ? 'primary' : undefined"
                                            :outlined="!wfhEntry(p, date)"
                                            :disabled="!p.permissions.can_edit || date < today || startedOn(p, date) || lockedWeek(p, week)"
                                            :loading="saving === date"
                                            @click="toggleWfh(p, date)"
                                        >
                                            {{ fmtDay(date) }}
                                        </v-btn>
                                    </div>
                                </td>
                                <td class="caption text-right text-no-wrap" style="width: 1px;">
                                    <template v-if="wfhIn(p, week)">
                                        <v-icon x-small color="primary">mdi-home</v-icon> {{ fmtDay(wfhIn(p, week).from_date) }}
                                        <v-icon v-if="wfhIn(p, week).approved && p.status !== 'approved'" x-small color="success" title="Approved">mdi-check-circle</v-icon>
                                        <span v-if="wfhIn(p, week).signed_in_at" class="grey--text">
                                            · {{ wfhIn(p, week).signed_in_at }}–{{ wfhIn(p, week).signed_out_at || '…' }}
                                        </span>
                                    </template>
                                </td>
                            </tr>
                            <!-- An approved day: moved, a move waiting, coming to the office, not used — and the staff member's own actions -->
                            <tr v-if="dayShown(p, week)" :key="week.week_start + ':day'" class="day-row">
                                <td></td>
                                <td colspan="2" class="pb-2">
                                    <wfh-day-actions :entry-id="wfhIn(p, week).id" :date="wfhIn(p, week).from_date" :day="wfhIn(p, week).wfh_day" @changed="dayChanged" />
                                </td>
                            </tr>
                            </template>
                        </tbody>
                    </v-simple-table>
                </template>

                <!-- Leave and other types: date ranges -->
                <template v-else>
                    <v-simple-table dense class="mb-1">
                        <tbody>
                            <tr v-if="!p.entries.length">
                                <td class="caption grey--text py-2">Nothing planned.</td>
                            </tr>
                            <tr v-for="e in p.entries" :key="e.id">
                                <td class="text-no-wrap py-1">{{ fmtRange(e.from_date, e.to_date) }}</td>
                                <td class="caption text-no-wrap">{{ e.days }} day{{ e.days === 1 ? '' : 's' }}</td>
                                <td class="caption">
                                    <template v-if="e.is_leave">
                                        <span v-if="e.applied_days >= e.days" class="green--text text--darken-2">
                                            <v-icon x-small color="green darken-2">mdi-check-circle</v-icon> Applied
                                        </span>
                                        <span v-else-if="e.applied_days > 0" class="orange--text">Applied {{ e.applied_days }} of {{ e.days }}</span>
                                        <span v-else class="grey--text">Not applied yet — apply through Leave</span>
                                    </template>
                                    <span v-if="e.approved && p.status !== 'approved'" class="d-block green--text text--darken-2">Approved</span>
                                    <span v-if="e.notes" class="d-block grey--text text--darken-1">{{ e.notes }}</span>
                                    <span v-if="e.change_reason" class="d-block orange--text text--darken-2">Changed after approval: {{ e.change_reason }}</span>
                                </td>
                                <td class="text-right" style="width: 1px;">
                                    <v-btn v-if="p.permissions.can_edit && e.from_date >= today && !lockedEntry(p, e)" icon x-small
                                        :loading="saving === 'e' + e.id" @click="remove(p, e)">
                                        <v-icon small>mdi-delete-outline</v-icon>
                                    </v-btn>
                                </td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                    <div v-if="p.permissions.can_edit" class="px-3 pb-2">
                        <v-btn small text color="primary" class="px-1" @click="openAdd(p)">
                            <v-icon small left>mdi-plus</v-icon>Add {{ p.label }}
                        </v-btn>
                    </div>
                </template>
            </v-card>

            <activity-log v-if="history && staffId" :key="'log' + logVersion" model="MonthlySchedule" :ref_id="staffId" max-height="320px" />
        </template>

        <!-- Add a leave / other entry -->
        <v-dialog v-model="addDialog.open" max-width="440px">
            <v-card v-if="addDialog.plan">
                <v-card-title class="subtitle-2">Add {{ addDialog.plan.label }}</v-card-title>
                <v-card-text>
                    <div class="d-flex" style="gap: 8px;">
                        <v-text-field v-model="addDialog.from_date" type="date" label="From" outlined dense
                            :min="firstEditableDay" :max="monthEnd"></v-text-field>
                        <v-text-field v-model="addDialog.to_date" type="date" label="To" outlined dense
                            :min="addDialog.from_date || firstEditableDay" :max="monthEnd"></v-text-field>
                    </div>
                    <v-textarea v-model="addDialog.notes" label="Notes (optional)" outlined dense auto-grow rows="2" counter="500"></v-textarea>
                    <div v-if="addDialog.plan.is_leave" class="caption grey--text">
                        This is a plan — you still apply for the leave through Leave as usual.
                    </div>
                    <div class="caption grey--text">An entry stays inside the month; split leave that runs into the next month.</div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn small text @click="addDialog.open = false">Cancel</v-btn>
                    <v-btn small color="primary" :disabled="!addDialog.from_date" @click="confirmAdd">Add</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Reason for a change: an approved plan, or HR's change -->
        <v-dialog v-model="reasonDialog.open" max-width="420px">
            <v-card>
                <v-card-title class="subtitle-2">Reason for the change</v-card-title>
                <v-card-text>
                    <div class="caption mb-2">{{ reasonDialog.label }}</div>
                    <v-textarea v-model="reasonDialog.reason" label="Reason (required)" outlined dense auto-grow rows="3" counter="1000"></v-textarea>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn small text @click="reasonDialog.open = false">Cancel</v-btn>
                    <v-btn small color="primary" :disabled="!reasonDialog.reason.trim()" @click="confirmReason">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Send a plan back -->
        <v-dialog v-model="returnDialog.open" max-width="420px">
            <v-card v-if="returnDialog.plan">
                <v-card-title class="subtitle-2">Send back the {{ returnDialog.plan.label }} plan</v-card-title>
                <v-card-text>
                    <div class="caption mb-2">The staff member is told the reason, changes the plan and sends it again.</div>
                    <v-textarea v-model="returnDialog.reason" label="Reason (required)" outlined dense auto-grow rows="3" counter="1000"></v-textarea>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn small text @click="returnDialog.open = false">Cancel</v-btn>
                    <v-btn small color="error" :disabled="!returnDialog.reason.trim()" :loading="acting === returnDialog.plan.type + ':return'"
                        @click="confirmReturn">Send back</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- HR cancels a plan -->
        <v-dialog v-model="cancelDialog.open" max-width="420px">
            <v-card v-if="cancelDialog.plan">
                <v-card-title class="subtitle-2">Cancel the {{ cancelDialog.plan.label }} plan for {{ monthLabel }}</v-card-title>
                <v-card-text>
                    <div class="caption mb-2">Its entries are removed — any already started stay. The staff member is told the reason.</div>
                    <v-textarea v-model="cancelDialog.reason" label="Reason (required)" outlined dense auto-grow rows="3" counter="1000"></v-textarea>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn small text @click="cancelDialog.open = false">Keep it</v-btn>
                    <v-btn small color="error" :disabled="!cancelDialog.reason.trim()" :loading="acting === cancelDialog.plan.type + ':cancel'"
                        @click="confirmCancel">Cancel plan</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
// One staff member's month: the signed-in user's own (no staffId) or a section
// head / HR opening a team member's. Each entry type is a plan, sent for
// approval and approved on its own. Every rule (deadline, WFH one per week and
// blocked weeks, balance, who may edit, send, approve, cancel, when a reason
// is needed) is decided by the backend (MonthlyScheduleService) — this shows
// each plan's `permissions`, the weeks and the entries, and sends the clicks.
// `history`: the staff member's schedule history (user_activities) below.
import ActivityLog from '../user-activity/ActivityLog.vue'
import WfhDayActions from './WfhDayActions.vue'

const ymd = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

const STATUS = {
    empty: { text: 'Nothing entered', color: 'grey lighten-1' },
    draft: { text: 'Not sent', color: 'grey' },
    returned: { text: 'Sent back', color: 'orange darken-1' },
    submitted: { text: 'Waiting for approval', color: 'blue' },
    approved: { text: 'Approved', color: 'success' },
}

export default {
    name: 'MonthSchedule',
    components: { ActivityLog, WfhDayActions },
    props: {
        year: { type: Number, required: true },
        month: { type: Number, required: true },
        staffId: { type: Number, default: null },
        history: { type: Boolean, default: false },
    },
    data: () => ({
        data: null,
        loading: false,
        saving: null,
        acting: null,
        logVersion: 0, // reload the history after a change
        addDialog: { open: false, plan: null, from_date: '', to_date: '', notes: '' },
        reasonDialog: { open: false, label: '', reason: '', run: null },
        returnDialog: { open: false, plan: null, reason: '' },
        cancelDialog: { open: false, plan: null, reason: '' },
    }),
    computed: {
        apiBase() {
            return this.staffId ? `/api/monthly-schedule/staff/${this.staffId}` : '/api/monthly-schedule/my'
        },
        today() {
            return ymd(new Date())
        },
        monthStart() {
            return `${this.year}-${String(this.month).padStart(2, '0')}-01`
        },
        monthEnd() {
            return ymd(new Date(this.year, this.month, 0))
        },
        monthLabel() {
            return new Date(this.year, this.month - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
        },
        firstEditableDay() {
            return this.today > this.monthStart ? this.today : this.monthStart
        },
        /** The latest time to tell HR you're coming to the office on a WFH day (from the backend). */
        noticeUntil() {
            const p = this.data && this.data.plans.find(x => x.is_wfh)
            const e = p && p.entries.find(x => x.wfh_day)
            return e ? e.wfh_day.office_notice_until.slice(11, 16) : '09:15'
        },
    },
    watch: {
        year() { this.load() },
        month() { this.load() },
        staffId() { this.load() },
    },
    mounted() {
        this.load()
    },
    methods: {
        load() {
            this.loading = true
            this.$axios.get(this.apiBase, { params: { year: this.year, month: this.month } })
                .then(resp => { this.data = resp.data })
                .finally(() => this.loading = false)
        },
        statusText(status) {
            return (STATUS[status] || STATUS.empty).text
        },
        statusColor(status) {
            return (STATUS[status] || STATUS.empty).color
        },
        trail(p) {
            if (p.status === 'approved') return `by ${p.approved_by} · ${p.approved_at}`
            if (p.status === 'submitted') return `sent ${p.submitted_at}`
            return ''
        },
        wfhEntry(p, date) {
            return p.entries.find(e => e.from_date === date)
        },
        /** Signed in for WFH on that day (attendance now) or given up for the office: it can't be moved. */
        startedOn(p, date) {
            const e = this.wfhEntry(p, date)
            return !!(e && (e.signed_in_at || (e.wfh_day && e.wfh_day.forgone_at)))
        },
        /** The approved day of the week has something to show or do (WfhDayActions). */
        dayShown(p, week) {
            const e = this.wfhIn(p, week)
            const d = e && e.wfh_day
            return !!(d && (d.moved_from || d.move || d.forgone_at || ['unused', 'lapsed'].includes(d.state)
                || d.can_forgo || d.can_unforgo || d.move_dates.length || d.can_cancel_move))
        },
        dayChanged() {
            this.load()
            this.logVersion++
            this.$emit('changed')
        },
        wfhIn(p, week) {
            return p.entries.find(e => week.dates.includes(e.from_date))
        },
        /** An approved entry in their own plan (e.g. entered by HR): only the section head or HR change it. */
        lockedEntry(p, e) {
            return p.permissions.mode === 'self' && e.approved
        },
        /** Their own week whose WFH day is approved (picking another day would move it), or a week given up for the office. */
        lockedWeek(p, week) {
            const e = this.wfhIn(p, week)
            return !!(e && (this.lockedEntry(p, e) || (e.wfh_day && e.wfh_day.forgone_at)))
        },
        toggleWfh(p, date) {
            const e = this.wfhEntry(p, date)
            if (e) {
                this.withReason(p, `Remove ${p.label} on ${this.fmtDay(date)}`, reason => this.send('del', e.id, null, date, reason))
            } else {
                this.withReason(p, `Set ${p.label} on ${this.fmtDay(date)}`,
                    reason => this.send('add', null, { type: p.type, from_date: date }, date, reason))
            }
        },
        openAdd(p) {
            this.addDialog = { open: true, plan: p, from_date: '', to_date: '', notes: '' }
        },
        confirmAdd() {
            const d = this.addDialog
            const body = { type: d.plan.type, from_date: d.from_date, to_date: d.to_date || d.from_date, notes: d.notes }
            this.addDialog.open = false
            this.withReason(d.plan, `Add ${d.plan.label} ${this.fmtRange(body.from_date, body.to_date)}`,
                reason => this.send('add', null, body, 'add', reason))
        },
        remove(p, e) {
            this.withReason(p, `Remove ${p.label} ${this.fmtRange(e.from_date, e.to_date)}`, reason => this.send('del', e.id, null, 'e' + e.id, reason))
        },
        /** After approval the backend needs a reason: ask for it first, otherwise run straight away. */
        withReason(p, label, run) {
            if (p.permissions.needs_reason) {
                this.reasonDialog = { open: true, label, reason: '', run }
                return
            }
            run(null)
        },
        confirmReason() {
            const { run, reason } = this.reasonDialog
            this.reasonDialog.open = false
            run(reason.trim())
        },
        send(kind, entryId, body, savingKey, reason) {
            this.saving = savingKey
            const req = kind === 'add'
                ? this.$axios.post(`${this.apiBase}/entries`, { ...body, reason })
                : this.$axios.delete(`${this.apiBase}/entries/${entryId}`, { data: { reason } })
            req.then(resp => {
                this.data = resp.data
                this.logVersion++
                this.$emit('changed')
            }).finally(() => this.saving = null)
        },
        async submit(p) {
            const ok = await this.$confirm({
                title: `Send your ${p.label} plan for approval?`,
                text: `Your section head approves your ${p.label} plan for ${this.monthLabel}. `
                    + `It's locked while it waits — withdraw it if you need to change it before ${this.fmtFullDate(this.data.deadline)}.`,
                icon: 'info',
                confirmText: 'Send',
            })
            if (!ok) return
            this.act(p, 'submit', '/api/monthly-schedule/my/submit')
        },
        withdraw(p) {
            this.act(p, 'withdraw', '/api/monthly-schedule/my/withdraw')
        },
        approve(p) {
            this.act(p, 'approve', `/api/monthly-schedule/staff/${this.staffId}/approve`)
        },
        openReturn(p) {
            this.returnDialog = { open: true, plan: p, reason: '' }
        },
        confirmReturn() {
            const { plan, reason } = this.returnDialog
            this.act(plan, 'return', `/api/monthly-schedule/staff/${this.staffId}/return`, { reason: reason.trim() })
                .then(() => { this.returnDialog.open = false })
        },
        openCancel(p) {
            this.cancelDialog = { open: true, plan: p, reason: '' }
        },
        confirmCancel() {
            const { plan, reason } = this.cancelDialog
            this.act(plan, 'cancel', `/api/monthly-schedule/staff/${this.staffId}/cancel`, { reason: reason.trim() })
                .then(() => { this.cancelDialog.open = false })
        },
        act(p, action, url, extra = {}) {
            this.acting = `${p.type}:${action}`
            return this.$axios.post(url, { year: this.year, month: this.month, type: p.type, ...extra })
                .then(resp => {
                    this.data = resp.data.data
                    this.logVersion++
                    this.$emit('changed')
                })
                .finally(() => this.acting = null)
        },
        blockedText(week) {
            const b = week.block || {}
            if (week.blocked === 'not_open') return "WFH isn't open this week"
            if (week.blocked === 'public_holiday') {
                return 'No WFH this week — ' + b.holidays.map(h => `${h.name} (${this.fmtDate(h.date)})`).join(', ')
            }
            if (week.blocked === 'position') return `The position ${b.position} isn't eligible for WFH`
            if (week.blocked === 'service_year') {
                return b.eligible_from
                    ? `WFH starts after ${b.months} months of service (from ${this.fmtFullDate(b.eligible_from)})`
                    : 'Joining date not recorded — ask HR'
            }
            return ''
        },
        fmtRange(from, to) {
            return !to || from === to ? this.fmtDay(from) : `${this.fmtDay(from)} – ${this.fmtDay(to)}`
        },
        fmtDate(s) {
            const [, m, d] = s.split('-')
            return `${d}/${m}`
        },
        fmtFullDate(s) {
            const [y, m, d] = s.split('-')
            return `${d}/${m}/${y}`
        },
        fmtDay(s) {
            const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][new Date(`${s}T00:00:00`).getDay()]
            return `${day} ${this.fmtDate(s)}`
        },
    },
}
</script>

<style scoped>
/* The week row and its day row read as one: no line between them. */
.has-day td {
    border-bottom: none !important;
}
.day-row td {
    height: auto !important;
}
.type-dot {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-right: 6px;
    vertical-align: middle;
}
</style>
